import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card } from "@acme/ui/card";
import { Button } from "@acme/ui/button";
import { Input } from "@acme/ui/input";
import { api } from "~/common/utils/trpc.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useGetIsFormReadyToSubmit from "~/common/hooks/useGetIsFormReadyToSubmit.js";

export const Route = createFileRoute("/_authed/my-players/$tag/social-media")({
  component: SocialMediaTab,
});

const socialProfilesFormSchema = z.object({
  youtube: z.string().max(100).optional(),
  twitch: z.string().max(100).optional(),
  tiktok: z.string().max(100).optional(),
  instagram: z.string().max(100).optional(),
});

type SocialProfilesForm = z.infer<typeof socialProfilesFormSchema>;

/** Matches backend player.getSocialProfilesByTag output. */
interface SocialProfilesData
{
  youtube: string | null;
  twitch: string | null;
  tiktok: string | null;
  instagram: string | null;
}

const PLATFORMS: {
  key: keyof SocialProfilesForm;
  label: string;
  placeholder: string;
  accent: string;
  icon: string;
}[] = [
  {
    key: "youtube",
    label: "YouTube",
    placeholder: "Username or channel",
    accent: "border-l-red-600 bg-red-500/5",
    icon: "▶",
  },
  { key: "twitch", label: "Twitch", placeholder: "Username", accent: "border-l-purple-600 bg-purple-500/5", icon: "⌗" },
  { key: "tiktok", label: "TikTok", placeholder: "@handle", accent: "border-l-pink-600 bg-pink-500/5", icon: "♪" },
  {
    key: "instagram",
    label: "Instagram",
    placeholder: "Username",
    accent: "border-l-amber-600 bg-amber-500/5",
    icon: "◇",
  },
];

function SocialMediaTab()
{
  const { tag } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: profilesData } = useQuery(api.player.getSocialProfilesByTag.queryOptions({ playerTag: tag }));
  const profiles = profilesData as SocialProfilesData | undefined;

  const form = useForm<SocialProfilesForm>({
    resolver: zodResolver(socialProfilesFormSchema),
    defaultValues: {
      youtube: "",
      twitch: "",
      tiktok: "",
      instagram: "",
    },
    mode: "onChange",
  });

  const { isFormReadyToSubmit } = useGetIsFormReadyToSubmit({ control: form.control });

  useEffect(() =>
  {
    if (profiles == null) return;
    form.reset({
      youtube: profiles.youtube ?? "",
      twitch: profiles.twitch ?? "",
      tiktok: profiles.tiktok ?? "",
      instagram: profiles.instagram ?? "",
    });
  }, [profiles, form.reset]);

  const upsert = useMutation(
    api.player.upsertSocialProfiles.mutationOptions({
      onSuccess: () =>
      {
        void queryClient.invalidateQueries({
          queryKey: api.player.getSocialProfilesByTag.queryKey({ playerTag: tag }),
        });
      },
    })
  );

  const onSubmit = form.handleSubmit((values) =>
  {
    upsert.mutate({
      playerTag: tag,
      youtube: values.youtube || undefined,
      twitch: values.twitch || undefined,
      tiktok: values.tiktok || undefined,
      instagram: values.instagram || undefined,
    });
  });

  const watched = form.watch();

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">Social media</h2>
          <p className="text-sm text-muted-foreground">
            Link your player to YouTube, Twitch, TikTok, and Instagram. Shown as icon + username.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {PLATFORMS.map(({ key, label, placeholder, accent, icon }) => (
              <Controller
                key={key}
                control={form.control}
                name={key}
                render={({ field, fieldState }) => (
                  <div className={`rounded-xl border border-input pl-4 pr-4 py-3 border-l-4 ${accent}`}>
                    <div className="flex items-center gap-3">
                      <span
                        className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-lg font-bold text-muted-foreground"
                        aria-hidden>
                        {icon}
                      </span>
                      <div className="min-w-0 flex-1 space-y-1">
                        <label htmlFor={field.name} className="text-xs font-medium text-muted-foreground">
                          {label}
                        </label>
                        <Input
                          {...field}
                          id={field.name}
                          placeholder={placeholder}
                          className="h-9 bg-background/80"
                          aria-invalid={fieldState.invalid}
                        />
                        {fieldState.error?.message && (
                          <p className="text-xs text-destructive">{fieldState.error.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              />
            ))}
          </div>

          {Object.values(watched).some(Boolean) && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Preview (icon + username)</p>
              <ul className="flex flex-wrap gap-2">
                {PLATFORMS.filter((p) => watched[p.key]).map(({ key, label, icon }) => (
                  <li key={key} className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-1 text-sm">
                    <span className="text-muted-foreground">{icon}</span>
                    <span>{watched[key] || label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button type="submit" disabled={!isFormReadyToSubmit || upsert.isPending}>
            Update
          </Button>
        </form>
      </div>
    </Card>
  );
}
